import React, { useState, useEffect, useMemo } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import {
  Btn,
  CustomDropdown,
  CustomModal,
  InputNumeroInteiro,
  CustomInputTextArea,
} from '../../../../../components'
import { getEmissoresByTps } from '../../../../../services/api/https/settings/emissor-cte.http'
import {
  getDadosEmissaoCte,
  postEmitCTe,
} from '../../../../../services/api/https/deliveries/cte.http'
import { getCfops } from '../../../../../services/api/https/settings/cfop.http'
import { getValorFrete } from '../../../../../services/api/https/registration/regras-frete.http'
import ValoresFiscaisCte from '../../Cte/ValoresFiscaisCte/ValoresFiscaisCte'
import {
  VALORES_FISCAIS_INICIAIS,
  aplicarTotal,
  erroNumeroOrdemColeta,
  erroValoresFiscais,
  montarComponentesValor,
  novoComponente,
  somaComponentes,
  toNum,
} from '../../Cte/ValoresFiscaisCte/valoresFiscaisCte.util'
import useFormularioCte from '../../Cte/Partes/useFormularioCte'
import SecaoPartes from '../../Cte/Partes/SecaoPartes'
import SecaoTomador from '../../Cte/Partes/SecaoTomador'
import SecaoPrestacao from '../../Cte/Partes/SecaoPrestacao'
import SecaoCarga from '../../Cte/Partes/SecaoCarga'
import SecaoDocumentosCarga from '../../Cte/Partes/SecaoDocumentosCarga'
import useEmissorUnico from '../../Cte/Partes/useEmissorUnico'

function formatarValor(v) {
  const n = toNum(v)
  return n != null ? n.toFixed(2).replace('.', ',') : ''
}

const inicialState = {
  emissor_id: null,
  cfop_id: null,
  numero_ordem_coleta: null,
  ...VALORES_FISCAIS_INICIAIS,
  observacoes: null,
}

export default function ModalEmitirCte({
  selectedTp,
  visible,
  delivery,
  // Emissão consolidada (CTe Empresa-Cliente): { deliveries_ids, frete, componentes, faixa_km_name, regras }
  consolidado = null,
  vehicleBadge,
  onModalClose,
  onModalSave,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [state, setState] = useState(inicialState)
  const [emissores, setEmissores] = useState([])
  const [cfops, setCfops] = useState([])
  const [componentes, setComponentes] = useState([])
  const [faixaKmName, setFaixaKmName] = useState(null)
  const [regrasFaixa, setRegrasFaixa] = useState([])
  const [showRegrasModal, setShowRegrasModal] = useState(false)
  const [documentos, setDocumentos] = useState([])
  const [carregandoDados, setCarregandoDados] = useState(false)
  const form = useFormularioCte({
    visible,
    onError,
    origemRemetente: 'empresa',
    origemDestinatario: 'cliente',
  })

  const deliveriesIds = useMemo(
    () => (consolidado ? consolidado.deliveries_ids : delivery ? [delivery.delivery_id] : []),
    [delivery, consolidado],
  )
  const { aplicarDadosIniciais } = form

  useEffect(() => {
    // O modal fica montado entre aberturas: uma resposta atrasada de uma abertura anterior não
    // pode sobrescrever os dados do CTe atual
    let aberturaAtiva = true

    function aplicarFrete(frete, componentesIniciais, faixaKm, regras) {
      setComponentes((componentesIniciais || []).map(c => novoComponente(c.nome, c.valor)))
      setFaixaKmName(faixaKm)
      setRegrasFaixa(regras || [])
      setState(prevState => ({
        ...prevState,
        valor_total: frete,
        valor_receber: frete,
        icms_base_calculo: frete,
        ibs_cbs_base_calculo: frete,
        observacoes: vehicleBadge ? `Veículo ${vehicleBadge}` : '',
      }))
    }

    async function fetchFrete() {
      try {
        const valorFrete = await getValorFrete(delivery.delivery_id)
        if (!aberturaAtiva) return
        aplicarFrete(
          valorFrete.frete,
          valorFrete.componentes,
          valorFrete.faixa_km_name,
          valorFrete.componentes,
        )
      } catch (error) {
        console.error('Erro ao buscar valor do frete:', error)
      }
    }

    async function fetchDados() {
      try {
        setCarregandoDados(true)
        const dados = await getDadosEmissaoCte(deliveriesIds)
        if (!aberturaAtiva) return
        aplicarDadosIniciais(dados)
        setDocumentos(dados.documentos || [])
      } catch (error) {
        if (aberturaAtiva) onError(error)
      } finally {
        if (aberturaAtiva) setCarregandoDados(false)
      }
    }

    if (!visible || !deliveriesIds.length) return undefined
    setDocumentos([])
    if (consolidado) {
      // Dados já calculados no modal Emitir CTe Empresa-Cliente
      aplicarFrete(
        consolidado.frete,
        consolidado.componentes,
        consolidado.faixa_km_name,
        consolidado.regras,
      )
    } else {
      fetchFrete()
    }
    fetchDados()
    return () => {
      aberturaAtiva = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, deliveriesIds, vehicleBadge])

  useEffect(() => {
    async function fetchListas() {
      try {
        const [listaEmissores, listaCfops] = await Promise.all([
          getEmissoresByTps(selectedTp),
          getCfops(),
        ])
        setEmissores(listaEmissores)
        setCfops(
          listaCfops.map(cfop => ({
            ...cfop,
            codigoNatureza: `${cfop.codigo} - ${cfop.natureza}`,
          })),
        )
      } catch (error) {
        onError(error)
      }
    }
    fetchListas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTp])

  const emissorSelecionado = emissores.find(item => item.id === state.emissor_id) || null
  useEmissorUnico({
    visible,
    emissores,
    selecionado: emissorSelecionado,
    onSelecionar: emissor => setState(prevState => ({ ...prevState, emissor_id: emissor.id })),
  })
  const cfopSelecionado = cfops.find(item => item.id === state.cfop_id) || null
  const ufEmissor = emissorSelecionado ? emissorSelecionado.uf : null

  // A Regra de Frete escolhida vira um componente do valor e entra no total do frete
  const onRegraSelecionada = regra => {
    const novos = [...componentes, novoComponente(regra.nome, regra.valor)]
    setComponentes(novos)
    setState(prevState => aplicarTotal(prevState, somaComponentes(novos)))
    setShowRegrasModal(false)
  }

  const validar = () => {
    if (!emissorSelecionado) return 'O campo Emissor é obrigatório'
    const erroOrdemColeta = erroNumeroOrdemColeta(state.numero_ordem_coleta)
    if (erroOrdemColeta) return erroOrdemColeta
    const erroFormulario = form.validar({ cfop: cfopSelecionado, ufEmissor })
    if (erroFormulario) return erroFormulario
    const erroFiscal = erroValoresFiscais(state)
    if (erroFiscal) return erroFiscal
    if (state.observacoes && state.observacoes.length > 2000) {
      return 'O campo Observações deve ter no máximo 2000 caracteres'
    }
    return null
  }

  // Events
  const onBtnSalvarClicked = async () => {
    try {
      const erro = validar()
      if (erro) throw new Error(erro)

      setIsLoading(true)

      const componentesValor = montarComponentesValor(componentes)

      await postEmitCTe({
        ...state,
        deliveries_ids: deliveriesIds,
        ...form.montarPayload(),
        ...(componentesValor.length ? { componentes_valor: componentesValor } : {}),
      })

      onModalSave(deliveriesIds[0])
    } catch (error) {
      onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe'}
      width={900}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <form className="p-grid" onSubmit={e => e.preventDefault()}>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="razao_social"
            options={emissores}
            value={emissorSelecionado}
            label="Emissor:*"
            filter={true}
            onChange={value => setState(prevState => ({ ...prevState, emissor_id: value.id }))}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <InputNumeroInteiro
            label="Nº Ordem Coleta:*"
            marginTop={10}
            value={state.numero_ordem_coleta}
            onChange={value => setState({ ...state, numero_ordem_coleta: value })}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Número gerado pela transportadora associado a ordem de coleta
          </span>
        </div>

        <SecaoPartes form={form} selectedTp={selectedTp} onError={onError} />
        <SecaoTomador form={form} selectedTp={selectedTp} onError={onError} />
        <SecaoPrestacao
          form={form}
          cfops={cfops}
          cfopSelecionado={cfopSelecionado}
          onCfopChange={cfop =>
            setState(prevState => ({ ...prevState, cfop_id: cfop ? cfop.id : null }))
          }
          ufEmissor={ufEmissor}
        />
        <SecaoCarga
          carga={form.carga}
          onChange={form.setCarga}
          onUnidadeChange={form.onUnidadeChange}
          dica="Pré-preenchida com a soma das notas; ajuste se necessário."
        />
        <SecaoDocumentosCarga documentos={documentos} carregando={carregandoDados} />

        <ValoresFiscaisCte
          key={visible ? 'aberto' : 'fechado'}
          state={state}
          onStateChange={setState}
          componentes={componentes}
          onComponentesChange={setComponentes}
          onError={onError}
          componentesActions={
            <Btn onClick={() => setShowRegrasModal(true)}>Nova Regra de Frete</Btn>
          }
        />

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observações"
            marginTop={10}
            rows={4}
            value={state.observacoes || ''}
            onChange={value => setState({ ...state, observacoes: value })}
          />
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnSalvarClicked} isLoading={isLoading}>
            Salvar
          </Btn>
        </div>
      </form>

      {/* Regras de Frete da Faixa de KM da nota */}
      <CustomModal
        isVisible={showRegrasModal}
        header={`Regras de Frete${faixaKmName ? ` - ${faixaKmName}` : ''}`}
        width={600}
        onClose={() => setShowRegrasModal(false)}
      >
        <span style={{ color: 'gray', fontSize: 12 }}>
          Clique na regra desejada para adicioná-la aos Componentes do Valor
        </span>
        <DataTable
          value={regrasFaixa}
          selectionMode="single"
          onRowClick={e => onRegraSelecionada(e.data)}
          emptyMessage="Nenhuma regra de frete cadastrada para a faixa de KM dessa cidade"
          style={{ marginTop: 10 }}
        >
          <Column field="nome" header="Regra de Faturamento" />
          <Column field="tipo" header="Tipo" style={{ width: 140 }} />
          <Column
            field="valor"
            header="Valor"
            style={{ width: 140 }}
            body={r => formatarValor(r.valor)}
          />
        </DataTable>
      </CustomModal>
    </CustomModal>
  )
}
